<template>
  <layout :title="seo.title" :seo="seo">
    <highlightFormSection
      v-if="hasValidSection(highlightFormSection)"
      :backgroundImage="highlightFormSection.fields.backgroundImage.value"
      :altImage="highlightFormSection.fields.altImage.value"
      :title="highlightFormSection.fields.title.value"
      :subtitle="highlightFormSection.fields.subtitle.value"
      :data-form-id="dataFormId"
      :data-portal-id="dataPortalId"
    ></highlightFormSection>

    <VisualPreview
      v-if="hasValidSection(visualPreview)"
      :title="visualPreview.fields.title.value"
      :text="visualPreview.fields.text.value"
      :imageSrc="visualPreview.fields.imageSrc.value"
      :imageAlt="visualPreview.fields.imageAlt.value"
      :logos="visualPreview.fields.logos.value"
      :imageLeft="true"
    />

    <RowCardsSection
      v-if="hasValidCards"
      :title="rowCardsSection.fields.title.value"
      :elements="rowCardsSection.fields.cards.value"
    />

    <CallToAction
      v-if="hasValidSection(callToAction)"
      :title="callToAction.fields.title.value"
      :text="callToAction.fields.text.value"
      :buttonText="callToAction.fields.buttonText.value"
      :buttonLink="callToAction.fields.buttonLink.value"
    />

    <div class="py-14">
      <TargetAudience
        v-if="hasValidSection(targetAudience)"
        :title="targetAudience.fields.title.value"
        :subtitle="targetAudience.fields.subtitle?.value"
        :packages="targetAudience.fields.packages.value"
        :imageSrc="targetAudience.fields.imageSrc.value"
        :imageAlt="targetAudience.fields.imageAlt.value"
        :imageLeft="false"
      />
    </div>

    <div id="eBook-form">
      <div class="flex justify-center bg-[#503189] w-full py-12">
        <div class="max-w-xl w-full">
          <DownloadForm
            :data-form-id="dataFormId"
            :data-portal-id="dataPortalId"
          />
        </div>
      </div>
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(
    /* webpackChunkName: "layout" */ "../Components/Layout/LandingPage/Layout.vue"
  );

const HighlightFormSection = () =>
  import(
    /* webpackChunkName: "HighlightFormSection" */ "../Components/Layout/LandingPage/HighlightFormSection.vue"
  );
const VisualPreview = () =>
  import(
    /* webpackChunkName: "VisualPreview" */ "../Components/Layout/LandingPage/VisualPreview.vue"
  );
const CallToAction = () =>
  import(
    /* webpackChunkName: "CallToAction" */ "../Components/Layout/LandingPage/CallToAction.vue"
  );
const DownloadForm = () =>
  import(
    /* webpackChunkName: "DownloadForm" */ "../Components/Layout/LandingPage/DownloadForm.vue"
  );
const TargetAudience = () =>
  import(
    /* webpackChunkName: "TargetAudience" */ "../Components/Layout/LandingPage/TargetAudience.vue"
  );

const RowCardsSection = () =>
  import(
    /* webpackChunkName: "RowCardsSection" */ "./../Components/Layout/LandingPage/rowCardsSection.vue"
  );
const TextImageSection = () =>
  import(
    /* webpackChunkName: "TextImageSection" */ "./../Components/Layout/textImageSection.vue"
  );

export default {
  props: {
    errors: Object,
    highlightFormSection: {
      type: Object,
      default: () => ({}),
    },
    visualPreview: {
      type: Object,
      default: () => ({}),
    },
    rowCardsSection: {
      type: Object,
      default: () => ({}),
    },
    callToAction: {
      type: Object,
      default: () => ({}),
    },
    targetAudience: {
      type: Object,
      default: () => ({}),
    },
    seo: {
      type: Object,
      default: () => ({
        title: "Youverse - Landing Page",
        description: "",
        keywords: "",
      }),
    },
    dataFormId: {
      type: String,
      default: null,
    },
    dataPortalId: {
      type: String,
      default: null,
    },
  },
  components: {
    layout,
    HighlightFormSection,
    VisualPreview,
    CallToAction,
    DownloadForm,
    RowCardsSection,
    TextImageSection,
    TargetAudience,
  },

  methods: {
    hasValidSection(section) {
      if (
        !section ||
        typeof section !== "object" ||
        !section.fields ||
        typeof section.fields !== "object"
      ) {
        return false;
      }

      return Object.values(section.fields).some((field) => {
        if (!field || typeof field !== "object" || !("value" in field)) {
          return false;
        }

        const value = field.value;

        if (field.isTemplate && field.type === "image") {
          return false;
        }

        if (Array.isArray(value)) {
          const nonTemplateItems = value.filter(
            (item) => !(item && typeof item === "object" && item.isTemplate)
          );
          return nonTemplateItems.length > 0;
        }

        if (typeof value === "string") {
          return value.trim().length > 0;
        }

        return value !== null && value !== undefined;
      });
    },
    getSectionValue(section, fieldName, defaultValue = "") {
      try {
        if (
          !section ||
          typeof section !== "object" ||
          !section.fields ||
          typeof section.fields !== "object" ||
          !section.fields[fieldName] ||
          typeof section.fields[fieldName] !== "object" ||
          !("value" in section.fields[fieldName])
        ) {
          return defaultValue;
        }

        const value = section.fields[fieldName].value;

        if (value === null || value === undefined) {
          return defaultValue;
        }

        if (Array.isArray(defaultValue)) {
          return Array.isArray(value) ? value : defaultValue;
        }

        if (typeof defaultValue === "string") {
          return typeof value === "string"
            ? value
            : String(value || defaultValue);
        }

        if (typeof defaultValue === "number") {
          const num = Number(value);
          return isNaN(num) ? defaultValue : num;
        }

        if (typeof defaultValue === "boolean") {
          return Boolean(value);
        }

        return value;
      } catch (error) {
        return defaultValue;
      }
    },
  },

  computed: {
    hasValidCards() {
      const cards = this.getSectionValue(this.rowCardsSection, "cards", []);
      return Array.isArray(cards) && cards.length > 0;
    },
  },
};
</script>
